// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 📊 VIRTUAL TRAINING HISTORY COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getPlayerVirtualTrainingSessions } from '../../services/virtualTrainings';
import type { VirtualTrainingSessionWithDetails } from '../../types';
import './VirtualTrainingHistory.css';

export const VirtualTrainingHistory: React.FC = () => {
  const { player } = useAuth();
  const { t } = useTranslation();

  const [sessions, setSessions] = useState<VirtualTrainingSessionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'completed' | 'abandoned'>('all');

  useEffect(() => {
    loadSessions();
  }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = async () => {
    if (!player) return;

    setLoading(true);
    try {
      const data = await getPlayerVirtualTrainingSessions(player.id);
      setSessions(data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    return session.status === filter;
  });

  const formatDuration = (started: string, completed?: string | null): string => {
    if (!completed) return '-';
    
    const start = new Date(started);
    const end = new Date(completed);
    const minutes = Math.floor((end.getTime() - start.getTime()) / 60000);
    
    return `${minutes} min`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="virtual-training-history">
      <div className="history-header">
        <h2>{t('training.virtualTrainings.history')}</h2>
        
        {/* Filter Tabs */}
        <div className="history-filters">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            {t('training.virtualTrainings.all')} ({sessions.length})
          </button>
          <button
            className={filter === 'completed' ? 'active' : ''}
            onClick={() => setFilter('completed')}
          >
            {t('training.virtualTrainings.completed')} ({sessions.filter(s => s.status === 'completed').length})
          </button>
          <button
            className={filter === 'abandoned' ? 'active' : ''}
            onClick={() => setFilter('abandoned')}
          >
            {t('training.virtualTrainings.abandoned')} ({sessions.filter(s => s.status === 'abandoned').length})
          </button>
        </div>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="empty-state">
          <p>{t('training.virtualTrainings.noHistory')}</p>
        </div>
      ) : (
        <div className="sessions-list">
          {filteredSessions.map(session => (
            <div key={session.id} className={`session-card ${session.status}`}>
              <div className="session-header">
                <div className="session-title">
                  <h3>{(session.virtual_training as any)?.title || 'Unknown Training'}</h3>
                  <span className={`status-badge ${session.status}`}>
                    {t(`training.virtualTrainings.status.${session.status}`)}
                  </span>
                </div>
                <div className="session-date">
                  {formatDate(session.started_at)}
                </div>
              </div>

              <div className="session-stats">
                <div className="stat">
                  <span className="stat-label">{t('training.virtualTrainings.exercisesCompleted')}:</span>
                  <span className="stat-value">{session.completed_exercises}</span>
                </div>
                {session.status === 'completed' && (
                  <div className="stat">
                    <span className="stat-label">{t('training.virtualTrainings.duration')}:</span>
                    <span className="stat-value">
                      {formatDuration(session.started_at, session.completed_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

